import { DEFAULT_PROMPTBOOK_NAME } from "@/constants";
import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { usePermissions } from "@/features/Permissions/hooks/usePermissions";
import { createResource } from "@/features/Resources/actions/createResource";
import {
  create_promptbook_event,
  delete_workspace_complete_event,
  delete_workspace_start_event,
  rename_workspace_event,
} from "@/services/posthog/events";
import { useAnalytics } from "@/services/posthog/hooks";
import { Button, Flex, IconButton, Menu, MenuButton, MenuList, Stack, useDisclosure, useToast } from "@chakra-ui/react";
import { DeleteConfirmationModal, Icon, MenuItem, RenameItemModal, SectionHeader } from "@pufflig/prompt-studio-ui";
import { useState } from "react";
import { deleteWorkspace } from "../../actions/deleteWorkspace";
import { renameWorkspace } from "../../actions/renameWorkspace";

interface WorkspaceTitleProps {
  workspaceName: string;
  workspaceId: string;
  teamId?: string;
}

export const WorkspaceTitle = (props: WorkspaceTitleProps) => {
  const { workspaceName, workspaceId, teamId } = props;
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { isOpen: isRenameOpen, onClose: onCloseRename, onOpen: onOpenRename } = useDisclosure();
  const { navigateToPromptbook, navigateToHome } = useAppNavigation();
  const toast = useToast();
  const analytics = useAnalytics();

  const [isLoading, setLoading] = useState(false);

  const onOpenDeleteModal = () => {
    analytics.capture(delete_workspace_start_event, { from: "menu" });
    onOpen();
  };

  const { data: permissions } = usePermissions(teamId);
  const canEditWorkspaces = permissions?.canEditWorkspaces;
  const canDeleteWorkspaces = permissions?.canDeleteWorkspaces;
  const canCreateResources = permissions?.canCreateResources;

  const onDeleteWorkspace = async () => {
    if (!workspaceId) return;
    analytics.capture(delete_workspace_complete_event, { from: "modal" });
    const { error } = await deleteWorkspace(workspaceId);
    if (error) toast({ title: "Error deleting workspace", description: error.message, status: "error" });
    else navigateToHome();
  };

  const createPromptbook = async () => {
    analytics.capture(create_promptbook_event, { from: "template", template: "blank" });
    setLoading(true);
    const { resourceId, error } = await createResource(DEFAULT_PROMPTBOOK_NAME, workspaceId, "promptbook", teamId);
    if (error) toast({ title: "Error creating promptbook", description: error.message, status: "error" });
    else navigateToPromptbook(resourceId as string);
  };

  const onRenameWorkspace = async (newName: string) => {
    if (!workspaceId) return;
    analytics.capture(rename_workspace_event, { from: "modal" });
    await renameWorkspace(workspaceId, newName);
  };

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <SectionHeader title={workspaceName} textStyle="heading/1" />
      {canEditWorkspaces && (
        <Stack direction="row">
          <Menu>
            <MenuButton isDisabled={!canCreateResources} isLoading={isLoading} as={Button} size="lg" variant="primary">
              Create
            </MenuButton>
            <MenuList>
              <MenuItem leftIcon={<Icon name="book" />} primaryLabel="New Promptbook" onClick={createPromptbook} />
            </MenuList>
          </Menu>
          <Menu>
            <MenuButton
              isDisabled={!canEditWorkspaces}
              as={IconButton}
              size="lg"
              aria-label="Options"
              icon={<Icon name="more" />}
            />
            <MenuList>
              <MenuItem onClick={onOpenRename} primaryLabel="Rename Workspace" leftIcon={<Icon name="edit" />} />
              {canDeleteWorkspaces && (
                <MenuItem
                  primaryLabel="Delete Workspace"
                  leftIcon={<Icon name="trash" />}
                  onClick={onOpenDeleteModal}
                />
              )}
            </MenuList>
          </Menu>
        </Stack>
      )}
      <DeleteConfirmationModal
        headline="Delete workspace"
        notificationText="You are about to delete this workspace. This action cannot be undone. Are you sure?"
        buttonText="Delete workspace"
        isOpen={isOpen}
        onClose={onClose}
        onDelete={onDeleteWorkspace}
      />
      <RenameItemModal
        title="Rename workspace"
        buttonText="Rename workspace"
        defaultName={workspaceName}
        inputPlaceholder="Type a new name"
        isOpen={isRenameOpen}
        onClose={onCloseRename}
        onRename={onRenameWorkspace}
      />
    </Flex>
  );
};
